/**
 * Chat on/off toggle at the left end of the map controls bar (phones; desktop
 * toggles chat from the top bar). A single square button with an ink chat
 * glyph, sized like the bar's zoom pads.
 *
 *   on   → chat mode: the inline chat input (and, on your turn, the chat log)
 *   off  → the default view: the log off-turn, the turn controls on your turn
 *
 * Unread messages badge the button. Where the board has no inline chat
 * (completed game) "on" opens the conversation drawer instead; the parent
 * decides.
 */
import React from 'react'
import { CountBubble } from '../ui/CountBubble'
import { TouchableOpacity, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import Svg, { Path } from 'react-native-svg'
import { useTheme } from '../../hooks/useTheme'

export function MapChatToggle({
  controlSize,
  unreadCount,
  active,
  onToggle,
  square = false,
  labels,
  glyph = 'chat',
}: {
  controlSize: 26 | 30
  unreadCount: number
  active: boolean
  onToggle: () => void
  /** Desktop header: a square button the size of the menu button. */
  square?: boolean
  /** Accessibility labels when the toggle means something other than chat
   *  on/off (e.g. the persistent log's chat-only filter). */
  labels?: { on: string; off: string }
  /** 'log-chat': the combined log + chat glyph (phones, your turn — one
   *  button opens chat and the log together). */
  glyph?: 'chat' | 'log-chat'
}) {
  const { theme } = useTheme()
  const ink = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const width = square ? controlSize : controlSize === 26 ? 34 : 42
  const label = labels
    ? (active ? labels.on : labels.off)
    : active ? 'Chat off' : unreadCount > 0 ? `Chat on (${unreadCount} unread)` : 'Chat on'

  return (
    <TouchableOpacity
      onPress={onToggle}
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: active }}
      testID="game-chat-control"
      className={`${active ? 'bg-blood' : 'bg-paper'} border border-olive rounded-md items-center justify-center`}
      style={{ width, height: controlSize }}
    >
      {glyph === 'log-chat' ? (
        <LogChatGlyph size={controlSize === 26 ? 18 : 20} color={active ? '#fff' : ink} />
      ) : (
        <MaterialCommunityIcons name="chat-outline" size={controlSize === 26 ? 15 : 17} color={active ? '#fff' : ink} />
      )}
      {unreadCount > 0 && (
        <View pointerEvents="none" style={{ position: 'absolute', top: -6, right: -6 }}>
          <CountBubble count={unreadCount} accessibilityLabel={`${unreadCount} unread`} />
        </View>
      )}
    </TouchableOpacity>
  )
}

/**
 * Your turn only: log on/off at the right end of the map controls bar. On
 * swaps the turn controls for the activity log; chat, log or neither is ever
 * selected (off-turn the log is already the default view, so it's hidden).
 */
export function MapLogToggle({
  controlSize,
  active,
  onToggle,
  square = false,
}: {
  controlSize: 26 | 30
  active: boolean
  onToggle: () => void
  /** Desktop header: a square button the size of the menu button. */
  square?: boolean
}) {
  const { theme } = useTheme()
  const ink = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const width = square ? controlSize : controlSize === 26 ? 34 : 42
  return (
    <TouchableOpacity
      onPress={onToggle}
      accessibilityRole="switch"
      accessibilityLabel={active ? 'Log off' : 'Log on'}
      accessibilityState={{ checked: active }}
      testID="game-log-control"
      className={`${active ? 'bg-blood' : 'bg-paper'} border border-olive rounded-md items-center justify-center`}
      style={{ width, height: controlSize }}
    >
      <MaterialCommunityIcons name="script-text" size={controlSize === 26 ? 15 : 17} color={active ? '#fff' : ink} />
    </TouchableOpacity>
  )
}

/**
 * Log + chat glyph (packages/ui/assets/icons/log-chat.svg): a solid speech
 * bubble with two equal log-line cutouts (2.4 units thick, round ends, so
 * they read at button size). 24×24 viewBox; 18px in 26px buttons, 20px in
 * 30px ones.
 */
export const LOG_CHAT_GLYPH_PATH =
  'M5 3.5h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-8l-6 3v-3a3 3 0 0 1-3-3v-10a3 3 0 0 1 3-3ZM7.2 7.7h9.6a1.2 1.2 0 0 1 0 2.4H7.2a1.2 1.2 0 0 1 0-2.4Zm0 4.6h9.6a1.2 1.2 0 0 1 0 2.4H7.2a1.2 1.2 0 0 1 0-2.4Z'

function LogChatGlyph({ size, color }: { size: number; color: string }) {
  return (
    // No accessible prop: on web react-native-svg forwards it to the DOM
    // (a console error); the button around it carries the label.
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path fillRule="evenodd" d={LOG_CHAT_GLYPH_PATH} fill={color} />
    </Svg>
  )
}
